// Pre-create check (atomic-design Rule 3): the closest existing organism is
// `MarketplaceHomeOrganism` — rejected because the desk is not a browse surface. It composes three
// sibling molecules (resume / findings / registry strip) over one `useStudioDesk` read, and
// its job is routing the author back into work already in flight, not listing a catalogue.
// No existing organism owns that shape; the reusable parts are the three molecules, which
// are separate files for exactly that reason.
import React, { useEffect } from 'react';
import { useTranslation } from 'localization';
import { SkeletonAtom, MessageAtom, useApiClientsOrNull, useStudioDesk } from 'design-system/my-lib';
import type { DeskTemplateRef, DeskFinding, StudioDesk } from 'types/studio';
import DeskResumeListMolecule from './DeskResumeListMolecule';
import DeskFindingsListMolecule from './DeskFindingsListMolecule';
import DeskRegistryStripMolecule from './DeskRegistryStripMolecule';
import { StudioBuilder } from './builders';
import './StudioDeskOrganism.scss';

export interface StudioDeskOrganismProps {
  onOpenResume: (item: DeskTemplateRef, route: string) => void;
  onOpenFinding: (finding: DeskFinding, route: string) => void;
  onOpenBuilder: (builder: StudioBuilder) => void;
  /** Fires once per successful load, for `StudioDeskPage` to report `studio_desk_viewed`. */
  onDeskLoaded?: (desk: StudioDesk) => void;
  className?: string;
}

/**
 * The desk (spec SC1) — Resume, Needs attention and Registry strip, each an independently
 * failing region (SC1: "when the call fails, each region renders its own error state and the
 * Registry strip still renders from `STUDIO_BUILDERS`"). Sub-source `degraded` entries (a
 * partial, successful response) render a quiet per-region notice instead — the two failure modes
 * are deliberately distinct: a hard failure has no data at all, a degraded source has an honest
 * empty one.
 */
const StudioDeskOrganism: React.FC<StudioDeskOrganismProps> = ({
  onOpenResume,
  onOpenFinding,
  onOpenBuilder,
  onDeskLoaded,
  className = '',
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { desk, loading, error } = useStudioDesk(apiClients);
  const hasError = !!error;

  useEffect(() => {
    // `generatedAt` is server-stamped and empty on the hook's placeholder desk, so it is the
    // discriminator for "a response actually came back" — without it the callback also fires for
    // the not-yet-fetched placeholder (e.g. API clients never become available) and reports a
    // fabricated `recents: 0` view.
    if (!loading && !hasError && desk.generatedAt) onDeskLoaded?.(desk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasError, desk]);

  return (
    <div className={['studio-desk', className].filter(Boolean).join(' ')} data-testid="studio-desk-organism">
      <section className="studio-desk__region" aria-labelledby="studio-desk-resume-title">
        <h2 id="studio-desk-resume-title" className="studio-desk__region-title">
          {t('studio.desk.resume.title')}
        </h2>
        {loading && <SkeletonAtom count={3} height="2.5rem" />}
        {!loading && hasError && (
          <div data-testid="desk-resume-error">
            <MessageAtom severity="error" text={t('studio.desk.resume.loadFailed')} />
          </div>
        )}
        {!loading && !hasError && (
          <DeskResumeListMolecule
            items={desk.recents}
            degraded={desk.degraded.includes('recents')}
            onOpen={onOpenResume}
          />
        )}
      </section>

      <section className="studio-desk__region" aria-labelledby="studio-desk-findings-title">
        <h2 id="studio-desk-findings-title" className="studio-desk__region-title">
          {t('studio.desk.findings.title')}
        </h2>
        {loading && <SkeletonAtom count={3} height="3rem" />}
        {!loading && hasError && (
          <div data-testid="desk-findings-error">
            <MessageAtom severity="error" text={t('studio.desk.findings.loadFailed')} />
          </div>
        )}
        {!loading && !hasError && (
          <DeskFindingsListMolecule
            findings={desk.findings}
            degraded={desk.degraded.includes('findings')}
            truncated={desk.truncated}
            onOpen={onOpenFinding}
          />
        )}
      </section>

      <section className="studio-desk__region" aria-labelledby="studio-desk-registry-title">
        <h2 id="studio-desk-registry-title" className="studio-desk__region-title">
          {t('studio.desk.registry.title')}
        </h2>
        <DeskRegistryStripMolecule
          counts={desk.counts}
          countsUnavailable={hasError || desk.degraded.includes('counts')}
          onOpen={onOpenBuilder}
        />
      </section>
    </div>
  );
};

export default StudioDeskOrganism;
